"use client";

import { c as _c } from "react/compiler-runtime";
import { Typography, DirectionIcon, TailIcon, Spacer } from "@/shared/ui/atoms";
import { useState } from "react";
import { CustomButton } from "@/shared/ui/molecules";
import { EvidenceCard, ReviewRow, SubjectTitle } from "@/widgets/hospital/ui/molecules";
import { useRouter } from "next/navigation";
import { useQueryHospitalReviewAnalysis } from "@/entities/hospital/hooks/queries";
import { useLocale, useTranslations } from "next-intl";
import { getStickyHeaderHeight } from "@/shared/utils/getStickyHeaderHeight";
import { useHospitalOriginId } from "@/entities/hospital/model/hospital-origin-id";
import { getHospitalAnalysisDisplayLabel } from "@/entities/hospital/lib/analysis-label-localization";
import { isNegativeAnalysisLabel } from "@/entities/hospital/lib/negative-analysis-labels";
import { ReviewAnalysisResponse } from "@/entities/hospital/types/type";
import { EVALUATION_STAT, SELECTION_EVIDENCE, isSelectionEvidencePilot, sortByDistinctiveness, type ReviewCategoryGroup } from "@/entities/hospital/lib/selection-evidence";
const ReviewAnalysis = () => {
  const $ = _c(31);
  const t = useTranslations();
  const locale = useLocale();
  const router = useRouter();
  const hospitalId = useHospitalOriginId();
  const {
    data
  } = useQueryHospitalReviewAnalysis(hospitalId);
  let t0;
  if ($[0] !== locale) {
    t0 = groups => groups.flatMap(item => {
      const displayMajorCategory = getHospitalAnalysisDisplayLabel(item.majorCategory, locale);
      if (!displayMajorCategory) {
        return [];
      }
      const items = item.items.flatMap(row => {
        const displayMinorCategory = getHospitalAnalysisDisplayLabel(row.minorCategory, locale);
        return displayMinorCategory ? [{
          ...row,
          displayMinorCategory
        }] : [];
      });
      return items.length > 0 ? [{
        ...item,
        displayMajorCategory,
        items
      }] : [];
    });
    $[0] = locale;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const toDisplayGroups = t0;
  const rawGroups = data ?? [];
  const statGroups = toDisplayGroups(rawGroups.filter(_temp).slice(0, 9));
  const evidenceGroups = isSelectionEvidencePilot(hospitalId) ? rawGroups.filter(_temp2) : [];
  const evidenceItems = sortByDistinctiveness(toDisplayGroups(evidenceGroups).flatMap(_temp3));
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  let t1;
  if ($[2] !== t) {
    t1 = t("reviewAnalysis.tooltipContent");
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const tooltipContent = t1;
  const hasEvidence = evidenceItems.length > 0;
  const showStatTitle = hasEvidence && statGroups.length > 0;
  let t2;
  if ($[4] !== isDetailOpen) {
    t2 = () => {
      setIsDetailOpen(!isDetailOpen);
      const el = document.getElementById("hospital-customer-evaluation-analysis");
      if (isDetailOpen && el) {
        const y = el.getBoundingClientRect().top + window.scrollY - getStickyHeaderHeight();
        window.scrollTo({
          top: y
        });
      }
    };
    $[4] = isDetailOpen;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleClickDetailToggle = t2;
  let t3;
  if ($[6] !== hospitalId || $[7] !== router) {
    t3 = (majorCategory, minorCategory, group) => {
      const query = new URLSearchParams({
        major: majorCategory,
        minor: minorCategory,
        group
      });
      router.push(`/review-detail/${hospitalId}?${query.toString()}`);
    };
    $[6] = hospitalId;
    $[7] = router;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handleClickRow = t3;
  const formatPercent = _temp4;
  const blockTitle = _temp5;
  if (statGroups.length === 0 && evidenceItems.length === 0) {
    return null;
  }
  const visibleEvidence = isDetailOpen ? evidenceItems : evidenceItems.slice(0, 3);
  const t4 = "hospital-customer-evaluation-analysis";
  let t5;
  if ($[9] !== t) {
    t5 = t("reviewAnalysis.title");
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t5 || $[12] !== tooltipContent) {
    t6 = <SubjectTitle title={t5} tooltip={true} tooltipContent={tooltipContent} />;
    $[11] = t5;
    $[12] = tooltipContent;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const t7 = hasEvidence && <div className="flex flex-col gap-2 px-4 pt-4">{blockTitle(t("reviewAnalysis.selectionEvidence"))}<div className="flex flex-col gap-2.5 bg-gray-100 p-3 rounded-xl">{visibleEvidence.map((row_1, index) => <EvidenceCard key={`selection-evidence-${index}`} title={row_1.displayMinorCategory} count={t("reviewAnalysis.reviewCount", {
        count: row_1.minorCount
      })} quote={row_1.quote} onClick={() => handleClickRow(row_1.majorCategory, row_1.minorCategory, SELECTION_EVIDENCE)} />)}</div></div>;
  const t8 = !isDetailOpen ? <div className={`px-4 pb-[14px] flex flex-col gap-2.5 ${showStatTitle ? "pt-5" : "pt-4"}`}>{showStatTitle && blockTitle(t("reviewAnalysis.evaluationStats"))}{statGroups.map((item_3, index_0) => <ReviewRow key={`customer-evaluation-row-${index_0}`} label={item_3.displayMajorCategory} description={item_3.items[0].displayMinorCategory} percent={formatPercent(item_3.items[0].minorPercent)} stacked={locale === "en"} />)}</div> : <div className="flex flex-col gap-4 px-4 w-full"><Spacer size={1} />{showStatTitle && blockTitle(t("reviewAnalysis.evaluationStats"))}{statGroups.map((item_4, index_1) => <div key={`customer-evaluation-detail-category-${index_1}`}><Typography size={16} lineHeight={20} className="pt-1 pb-2 text-gray-900 font-bold">{item_4.displayMajorCategory}</Typography><div className="relative items-start pl-[9px] w-full"><div className="absolute top-[2px] left-0"><TailIcon /></div><div className="flex flex-col gap-2.5 bg-gray-100 p-3 rounded-xl">{item_4.items.map((row_2, rowIndex) => {
            const totalCount = item_4.items.reduce(_temp6, 0);
            const percent_0 = Math.round(row_2.minorCount / totalCount * 100);
            return <CustomButton key={`customer-evaluation-detail-row-${rowIndex}`} className="relative flex min-w-0 items-center justify-between gap-2 py-1.5 px-2 bg-white rounded-[5px]" onClick={() => handleClickRow(item_4.majorCategory, row_2.minorCategory, EVALUATION_STAT)}>{rowIndex === 0 && <div className="absolute left-0 top-0 h-full bg-gradient-to-r to-primary-200 from-[#D5EBFF] rounded-l-[5px] z-5" style={{
                width: `${percent_0}%`,
                borderRadius: `${percent_0 === 100 && "5px"}`
              }} />}<Typography size={14} lineHeight={18} className="relative z-6 min-w-0 text-left text-gray-900 font-semibold tracking-[-0.24px] break-words [overflow-wrap:anywhere]">{row_2.displayMinorCategory}</Typography><div className="relative z-6 flex shrink-0 items-center"><Typography size={12} lineHeight={18} className="text-gray-400 font-medium">{row_2.minorCount}</Typography><DirectionIcon size={20} rotate={270} className="text-gray-300" /></div></CustomButton>;
          })}</div></div></div>)}</div>;
  const t9 = `text-gray-900 font-${!isDetailOpen && "semibold"} tracking-[-0.28px] ${isDetailOpen ? "line-height-[22px]" : "line-height-[18px]"}`;
  let t10;
  if ($[14] !== isDetailOpen || $[15] !== t) {
    t10 = isDetailOpen ? t("reviewAnalysis.collapse") : t("reviewAnalysis.detail");
    $[14] = isDetailOpen;
    $[15] = t;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t9) {
    t11 = <Typography size={14} lineHeight={18} className={t9}>{t10}</Typography>;
    $[17] = t10;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const t12 = isDetailOpen ? 180 : 0;
  let t13;
  if ($[20] !== t12) {
    t13 = <DirectionIcon size={20} rotate={t12} className="text-gray-500" />;
    $[20] = t12;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== handleClickDetailToggle || $[23] !== t11 || $[24] !== t13) {
    t14 = <CustomButton className="w-full flex items-center justify-center gap-1 pt-2.5 pb-5" onClick={handleClickDetailToggle}>{t11}{t13}</CustomButton>;
    $[22] = handleClickDetailToggle;
    $[23] = t11;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t14 || $[27] !== t6 || $[28] !== t7 || $[29] !== t8) {
    t15 = <div id={t4}>{t6}{t7}{t8}{t14}</div>;
    $[26] = t14;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  return t15;
};
export default ReviewAnalysis;
function _temp(item_0) {
  return item_0.categoryGroup !== SELECTION_EVIDENCE;
}
function _temp2(item_1) {
  return item_1.categoryGroup === SELECTION_EVIDENCE;
}
function _temp3(item_2) {
  return item_2.items.map(row_0 => ({
    majorCategory: item_2.majorCategory,
    minorCategory: row_0.minorCategory,
    displayMinorCategory: row_0.displayMinorCategory,
    minorCount: row_0.minorCount,
    quote: isNegativeAnalysisLabel(row_0.minorCategory) ? undefined : row_0.representativeQuote
  }));
}
function _temp4(percent) {
  return percent.toFixed(1);
}
function _temp5(label) {
  return <Typography size={14} lineHeight={18} className="text-gray-800 font-bold tracking-[-0.28px]">{label}</Typography>;
}
function _temp6(acc, i) {
  return acc + i.minorCount;
}